import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Button, CircularProgress } from '@mui/material';
import { BorderColor } from '@mui/icons-material';
import useCreateTicket from './hooks/useCreateTicket';
import TicketBasicDetail from './forms/TicketBasicDetail';
import TicketScheduling from './forms/TicketScheduling';
import TicketAdvancedSetting from './forms/TicketAdvancedSettings';
import AlertDialog from '../../../../../../components/Dialog/AlertDialog';
import { Body1, Body2 } from '../../../../../../themes/typography';
import '../../../../../../styles/components/CreateTicket.css';

export default function CreateTicket() {
    const { loading, ticket } = useSelector((state) => state.ticket);
    const {
        formik,
        showDatesInInput,
        isSelectAllDates,
        isDateDropdownOpen,
        minStartTime,
        maxStartTime,
        minEndTime,
        maxEndTime,
        maxRangeDate,
        minRangeDate,
        handleDateSubmission,
        handleSlotSubmission,
        handleDateChange,
        handleSlotChange,
        onDateDropdownOpen,
        onSlotDropdownOpen,
        handleAllDatesSelect,
        handleAllSlotsSelect,
        preventBackSpaceKey,
        setShowDatesInInput,
        setShowSlotsInInput,
        setIsSelectAllSlots,
        mappedDates,
        mappedSlots,
        isSlotDropdownOpen,
        showSlotsInInput,
        setIsSelectAllDates,
        isSelectAllSlots,
        handleDateDropdownClose,
        handleSlotDropdownClose,
        handleCancelTicketCreation,
        isEditModeOn,
        shouldShowEditButton,
        exceedStatusEditable,
        qtyEditable,
        handleEditing,
        shouldAllFieldDisabled,
        gstRates,
        eventData,
        handleCheckInTimeSelection,
        openPaidTicketCheckModal,
        missingDetailsForPaidTicket,
        handlePaidTicketContinue,
        setOpenPaidTicketCheckModal
    } = useCreateTicket();

    // console.log("Missing Details for Paid Ticket:", JSON.stringify(missingDetailsForPaidTicket, null, 2));

    return (
        <Fragment>
            <form noValidate id="ticket-creation-form" onSubmit={formik.handleSubmit}>
                <div className="ticket-creation-form-container">
                    {shouldShowEditButton && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignSelf: 'stretch'
                            }}
                        >
                            <Button
                                variant="text"
                                disabled={isEditModeOn}
                                onClick={handleEditing}
                                startIcon={<BorderColor />}
                            >
                                Edit
                            </Button>
                        </div>
                    )}
                    <TicketBasicDetail
                        formik={formik}
                        isEditModeOn={shouldAllFieldDisabled}
                        exceedStatusEditable={exceedStatusEditable}
                        qtyEditable={qtyEditable}
                        gstRates={gstRates}
                    />
                    <TicketScheduling
                        formik={formik}
                        isEditModeOn={shouldAllFieldDisabled}
                        mappedDates={mappedDates}
                        mappedSlots={mappedSlots}
                        showDatesInInput={showDatesInInput}
                        isSelectAllDates={isSelectAllDates}
                        isDateDropdownOpen={isDateDropdownOpen}
                        minStartTime={minStartTime}
                        maxStartTime={maxStartTime}
                        minEndTime={minEndTime}
                        maxEndTime={maxEndTime}
                        maxRangeDate={maxRangeDate}
                        minRangeDate={minRangeDate}
                        handleDateSubmission={handleDateSubmission}
                        handleSlotSubmission={handleSlotSubmission}
                        handleDateChange={handleDateChange}
                        handleSlotChange={handleSlotChange}
                        onDateDropdownOpen={onDateDropdownOpen}
                        onSlotDropdownOpen={onSlotDropdownOpen}
                        handleAllDatesSelect={handleAllDatesSelect}
                        handleAllSlotsSelect={handleAllSlotsSelect}
                        preventBackSpaceKey={preventBackSpaceKey}
                        setShowDatesInInput={setShowDatesInInput}
                        setIsSelectAllDates={setIsSelectAllDates}
                        // setIsDateDropdownOpen={setIsDateDropdownOpen}
                        setShowSlotsInInput={setShowSlotsInInput}
                        setIsSelectAllSlots={setIsSelectAllSlots}
                        isSelectAllSlots={isSelectAllSlots}
                        isSlotDropdownOpen={isSlotDropdownOpen}
                        handleDateDropdownClose={handleDateDropdownClose}
                        handleSlotDropdownClose={handleSlotDropdownClose}
                        showSlotsInInput={showSlotsInInput}
                    />
                    <TicketAdvancedSetting
                        formik={formik}
                        isEditModeOn={shouldAllFieldDisabled}
                        eventData={eventData}
                        onCheckInTimeSelection={handleCheckInTimeSelection}
                    />
                </div>
                <div className="ticket-creation-action-btns">
                    <Button variant="text" onClick={handleCancelTicketCreation}>
                        Cancel
                    </Button>
                    <Button variant="main" disabled={loading && !openPaidTicketCheckModal} type="submit">
                        {ticket?.ticket_id ? 'Update Ticket' : 'Create Ticket'}{' '}
                        {loading && !openPaidTicketCheckModal && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                            />
                        )}
                    </Button>
                </div>
            </form>

            <AlertDialog
                open={openPaidTicketCheckModal}
                onCancel={() => setOpenPaidTicketCheckModal(false)}
                onConfirm={handlePaidTicketContinue}
                dialogTitle="Missing Details for Paid Ticket"
                dialogId='missing-details-modal'
                disabled={loading && openPaidTicketCheckModal}
                loading={loading && openPaidTicketCheckModal}
            >
                <MissingDetailsList missingDetails={missingDetailsForPaidTicket} />
            </AlertDialog>
        </Fragment>
    );
}

function MissingDetailsList({ missingDetails }) {
    return (
        <div className='missing-details-container'>
            <div className='missing-details-title'>
                <Body1>Your event will be paused due to creating a paid ticket. The following details are missing:</Body1>
            </div>

            <ul className='missing-details-list'>
                {missingDetails.length > 0 && missingDetails.map((detail) => (
                    <li key={detail.id}>
                        <Body2 fontWeight={500}>
                            {detail.title}
                        </Body2>
                    </li>
                ))}
            </ul>
        </div>
    )
}